import {action, computed, observable, observe} from "mobx";
import React, {ChangeEvent} from "react";
import {RootStore, WemChartStrategy} from "./rootStore";
import {bisectLeft, bisectRight} from "d3-array";
import parse from "csv-parse/lib/sync";
import {observer} from "mobx-react";
import {parse as parseDate} from "date-fns";
import {isMobile} from "react-device-detect";

export interface DataPoint {
  date: Date
  values: number[]
}

interface ChartMouseEvent {
  activeTooltipIndex?: number
}

export function dataUnitFromArray([date, ...values]: string[], dateFormat = 'd/M/y'): DataPoint {
  return {
    date: parseDate(date, dateFormat, new Date()),
    values: values.map(parseFloat),
  }
}

export const rateOfReturn = (from?: number, to?: number) => {
  return from === undefined || to === undefined ? undefined :
    // (to - from) / from
    // (to - from) / (from + 100)
    (to + 100) / (from + 100) - 1
}

export class ChartStore {
  readonly dataObservable = observable<DataPoint>([])
  readonly valueLabelsObservable = observable.array<string>([])
  readonly rootStore: RootStore
  @observable title: string
  @observable dataUrl: string
  @observable isDataLoaded = false
  @observable dateLabel = 'Date'
  @observable leftZoomDateInput = new Date()
  @observable rightZoomDateInput = new Date()
  @observable selectedDataColumn = 0
  @observable leftZoomPreview: number | undefined
  @observable rightZoomPreview: number | undefined
  @observable leftZoom = -1
  @observable rightZoom = 0
  @observable leftZoomShiftStart: number | undefined
  @observable rightZoomShiftStart: number | undefined
  @observable zoomShiftStart: number | undefined
  @observable zoomShiftEnd: number | undefined
  @observable error: string | undefined
  @observable width: number = 100
  readonly minPixelsPerDataPoint = 10  // pixels per data point

  constructor(rootStore: RootStore, dataUrl: WemChartStrategy) {
    this.rootStore = rootStore
    this.dataUrl = dataUrl.url
    this.title = dataUrl.name
  }

  readonly init = () => {
    const {isDataLoaded} = this
    if (!isDataLoaded) this.loadData()
    observe(this.dataObservable, this.zoomOut)
    observe(this, 'leftZoom', () => {
      this.leftZoomDateInput = this.dates[this.leftZoom] || this.firstDate!
    })
    observe(this, 'rightZoom', () => {
      this.rightZoomDateInput = this.dates[this.rightZoom] || this.lastDate!
    })
  }

  @computed get dataScale() {
    return this.computeScale(this.dataObservable)
  }

  @computed get data() {
    return this.dataObservable.toJS()
  }

  set data(data) {
    this.dataObservable.replace(data)
  }

  @computed get dataScaled() {
    return this.scaleData(this.data, this.dataScale)
  }

  @computed get zoomedData() {
    let {data} = this

    const {leftZoom: l, rightZoom: r} = this

    if (l !== undefined && r !== undefined && l !== r) {
      data = data.slice(l, r + 1)
    }

    return data
  }

  @computed get zoomedDataScale() {
    return this.computeScale(this.zoomedData)
  }

  @computed get zoomedDataScaled() {
    return this.scaleData(this.zoomedData, this.zoomedDataScale)
  }

  @computed get numValues() {
    const {data} = this
    return data[0] ? data[0].values.length : 0
  }

  @computed get valueLabels(): string[] {
    const specifiedValueLabels = this.valueLabelsObservable.toJS()

    return Array.from(Array(this.numValues)).map((_, i) => {
      const specified = specifiedValueLabels[i]
      return specified === undefined ? `${i + 1}` : specified
    })
  }

  set valueLabels(labels) {
    this.valueLabelsObservable.replace(labels)
  }

  @computed get selectedValueLabel() {
    return this.valueLabels[this.selectedDataColumn]
  }

  @computed get timestamps() {
    return this.data.map(r => r.date.getTime())
  }

  @computed get dates() {
    return this.data.map(r => new Date(r.date))
  }

  @computed get canShowZoomPreview() {
    return (
      this.leftZoomPreview !== undefined &&
      this.rightZoomPreview !== undefined
    )
  }

  @computed get canShowZoomPreviewInMainChart(): boolean {
    return (
      this.canShowZoomPreview
      && !!this.leftZoomedScaledDate
      && !!this.rightZoomedScaledDate
      && !this.zoomShiftInProgress
    )
  }

  @computed get canShowZoomPreviewInMiniMap(): boolean {
    return (
      !this.zoomShiftInProgress
      && this.canShowZoomPreview
      && !!this.leftZoomPreviewDate
      && !!this.rightZoomPreviewDate
    )
  }

  @computed get doShowZoomedRegionInMiniMap() {
    return this.leftZoom < this.data.length && this.isZoomed
  }

  @computed get isZoomed() {
    return this.leftZoom !== 0 || this.rightZoom !== this.data.length - 1
  }

  @computed get leftZoomed() {
    const {leftZoomPreview, leftZoom, rightZoom} = this
    return leftZoomPreview === undefined ? undefined :
      Math.min(rightZoom, Math.max(leftZoom, leftZoomPreview))
  }

  @computed get rightZoomed() {
    const {rightZoomPreview, leftZoom, rightZoom} = this
    return rightZoomPreview === undefined ? undefined :
      Math.min(rightZoom, Math.max(leftZoom, rightZoomPreview))
  }

  @computed get leftZoomDataPoint() {
    return this.data[this.leftZoom]
  }

  @computed get rightZoomDataPoint() {
    return this.data[this.rightZoom]
  }

  @computed get leftZoomedScaledDate() {
    return this.getScaledDataPoint(
      this.zoomedDataScaled, this.leftZoomed, this.zoomedDataScale, 'left', this.leftZoom)?.date
  }

  @computed get leftZoomedScaledTimestamp() {
    const {leftZoomedScaledDate} = this
    return leftZoomedScaledDate?.getTime()
  }

  @computed get rightZoomedScaledDate() {
    return this.getScaledDataPoint(
      this.zoomedDataScaled, this.rightZoomed, this.zoomedDataScale, 'right', this.leftZoom)?.date
  }

  @computed get rightZoomedScaledTimestamp() {
    const {rightZoomedScaledDate} = this
    return rightZoomedScaledDate?.getTime()
  }

  @computed get leftZoomPreviewDate() {
    return this.getScaledDataPoint(this.dataScaled, this.leftZoomPreview, this.dataScale, 'left')?.date
  }

  @computed get leftZoomPreviewTimestamp() {
    const {leftZoomPreviewDate} = this
    return leftZoomPreviewDate?.getTime()
  }

  @computed get rightZoomPreviewDate() {
    return this.getScaledDataPoint(this.dataScaled, this.rightZoomPreview, this.dataScale, 'right')?.date
  }

  @computed get rightZoomPreviewTimestamp() {
    const {rightZoomPreviewDate} = this
    return rightZoomPreviewDate?.getTime()
  }

  @computed get leftZoomScaledDataPoint() {
    return  this.getScaledDataPoint(this.dataScaled, this.leftZoom, this.dataScale, 'left')
  }

  @computed get leftZoomScaledDate() {
    return this.leftZoomScaledDataPoint?.date
  }

  @computed get leftZoomScaledTimestamp() {
    const {leftZoomScaledDate} = this
    return leftZoomScaledDate?.getTime()
  }

  @computed get rightZoomScaledDataPoint() {
    return  this.getScaledDataPoint(this.dataScaled, this.rightZoom, this.dataScale, 'right')
  }

  @computed get rightZoomScaledDate() {
    return this.rightZoomScaledDataPoint?.date
  }

  @computed get rightZoomScaledTimestamp() {
    const {rightZoomScaledDate} = this
    return rightZoomScaledDate?.getTime()
  }

  @computed get zoomShiftInProgress() {
    return this.zoomShiftStart !== undefined
  }

  @computed get zoomInProgress() {
    return this.leftZoomPreview !== undefined
  }

  @computed get canShiftZoom() {
    return this.zoomShiftStart !== undefined && this.zoomShiftEnd !== undefined
  }

  @computed get canZoomIn() {
    return this.leftZoomPreview !== undefined && this.rightZoomPreview !== undefined
  }

  @computed get firstDate() {
    const {data} = this
    return data.length > 0 ? new Date(data[0].date) : undefined
  }

  @computed get firstTimestamp() {
    const {data} = this
    return data.length > 0 ? data[0].date.getTime() : undefined
  }

  @computed get lastDate() {
    const {data} = this
    return data.length > 0 ? new Date(data[data.length - 1].date) : undefined
  }

  @computed get lastTimestamp() {
    const {data} = this
    return data.length > 0 ? data[data.length - 1].date.getTime() : undefined
  }

  @computed get leftZoomScaled() {
    return this.leftZoom / this.dataScale
  }

  @computed get rightZoomScaled() {
    return this.rightZoom / this.dataScale
  }

  @computed get leftZoomShiftStartScaled() {
    const {leftZoomShiftStart, dataScale} = this
    return leftZoomShiftStart === undefined ? undefined : leftZoomShiftStart / dataScale
  }

  @computed get rightZoomShiftStartScaled() {
    const {rightZoomShiftStart, dataScale} = this
    return rightZoomShiftStart === undefined ? undefined : rightZoomShiftStart / dataScale
  }

  @computed get zoomShiftStartScaled() {
    const {zoomShiftStart, dataScale} = this
    return zoomShiftStart === undefined ? undefined : zoomShiftStart / dataScale
  }

  @computed get zoomShiftEndScaled() {
    const {zoomShiftEnd, dataScale} = this
    return zoomShiftEnd === undefined ? undefined : zoomShiftEnd / dataScale
  }

  @computed get leftZoomPreviewScaled() {
    const {leftZoomPreview, dataScale} = this
    return leftZoomPreview === undefined ? undefined : leftZoomPreview / dataScale
  }

  @computed get rightZoomPreviewScaled() {
    const {rightZoomPreview, dataScale} = this
    return rightZoomPreview === undefined ? undefined : rightZoomPreview / dataScale
  }

  @computed get zoomedDataRateOfReturn() {
    return rateOfReturn(this.leftZoomDataPoint?.values[this.selectedDataColumn], this.rightZoomDataPoint?.values[this.selectedDataColumn])
  }

  readonly getScaledDataPoint = (data: DataPoint[], index: number | undefined, scale: number, leftOrRight: 'left' | 'right', offset = 0) => {
    if (index === undefined) return undefined
    const roundFn = leftOrRight === 'left' ? Math.floor : Math.ceil;
    const correctIndex = Math.min(data.length - 1, roundFn((index - offset) / scale))
    return data[correctIndex]
  };

  readonly computeScale = (data: Array<DataPoint>) => {
    const {width, minPixelsPerDataPoint} = this
    const pixelsPerDataPoint = Math.max(minPixelsPerDataPoint, width / data.length)
    return Math.max(1, Math.ceil(data.length * (pixelsPerDataPoint / width)))
  };

  readonly scaleData = (data: DataPoint[], scale: number) => {
    const result = []
    for (let i = 0; i < data.length; i += scale) {
      result.push(data[i])
    }
    if ((data.length - 1) % scale !== 0) {
      result.push(data[data.length-1])
    }
    return result
  };

  readonly valueGetter = (dataUnit: DataPoint) => {
    return dataUnit.values[this.selectedDataColumn]
  }

  private readonly canStartZoomShift = (x: number) => {
    const {isZoomed, rightZoom, leftZoom, data} = this
    return isZoomed && rightZoom - leftZoom < data.length && leftZoom <= x && x <= rightZoom
  };

  @action readonly loadData = () => {
    fetch(this.dataUrl)
      .then(response => {
        if (response.ok) {
          response.text().then(text => {
            try {
              let rawRows: string[][] = parse(text, this.rootStore?.settings?.csv)
              const firstRow = rawRows[0]
              if (firstRow !== undefined) {
                const v1 = firstRow[1]
                const v1Number = parseFloat(v1)
                if (v1 !== undefined && (v1Number === undefined || isNaN(v1Number))) {
                  this.dateLabel = firstRow[0]
                  this.valueLabels = firstRow.slice(1)
                  rawRows = rawRows.slice(1)
                }
              }
              const {rootStore: {settings}} = this
              this.data = rawRows.map(row => dataUnitFromArray(row, settings?.dateFormat))
              this.isDataLoaded = true
              console.log(`successfully loaded ${rawRows.length} rows of data for chart "${this.title}"`)
            } catch (e) {
              this.error = e.message
              console.error(e)
            }
          })
        } else {
          this.error = `${response.status} ${response.statusText}`
          console.error(this.error)
        }
      })
      .catch(error => {
        this.error = error.message
        console.error(this.error)
        console.error(`Couldn't fetch '${this.dataUrl}': ${error.message}`)
      })
  }

  @action readonly setSelectedDataColumn = (v: number) => {
    this.selectedDataColumn = v
  }

  @action readonly stopAllZoomChangeInProgress = () => {
    this.leftZoomPreview = undefined
    this.rightZoomPreview = undefined
    this.leftZoomShiftStart = undefined
    this.zoomShiftStart = undefined
  }

  @action readonly zoom = (left: number, right: number) => {
    this.leftZoom = left
    this.rightZoom = right
    this.stopAllZoomChangeInProgress()
  }

  @action readonly zoomOut = () => {
    this.zoom(0, this.data.length - 1)
  }

  @action readonly zoomIn = () => {
    let {leftZoomPreview, rightZoomPreview} = this

    if (
      rightZoomPreview === undefined ||
      leftZoomPreview === undefined ||
      leftZoomPreview === rightZoomPreview
    ) {
      this.zoomOut()
      return
    }

    if (leftZoomPreview > rightZoomPreview) {
      [leftZoomPreview, rightZoomPreview] = [rightZoomPreview, leftZoomPreview]
    }

    this.zoom(leftZoomPreview, rightZoomPreview)
  }

  @action readonly zoomShift = () => {
    let {
      leftZoomShiftStart: l,
      rightZoomShiftStart: r,
      zoomShiftStart: s,
      zoomShiftEnd: e,
    } = this

    if (
      l === undefined ||
      r === undefined ||
      s === undefined ||
      e === undefined
    ) {
      return
    }

    const diff = e - s

    l = Math.max(0, Math.min(this.data.length - 1, l + diff))
    r = Math.max(0, Math.min(this.data.length - 1, r + diff))

    this.leftZoom = l
    this.rightZoom = r
    this.leftZoomPreview = l
    this.rightZoomPreview = r
  }

  @action readonly handleChangeLeftZoomDate = (event: ChangeEvent<HTMLInputElement>) => {
    const date = event.target.valueAsDate
    const idx = date === null ? 0 : bisectLeft(this.dates, date)
    this.zoom(idx, this.rightZoom)
    this.leftZoomDateInput = date || this.firstDate || new Date()
  }

  @action readonly handleChangeRightZoomDate = (event: ChangeEvent<HTMLInputElement>) => {
    const date = event.target.valueAsDate
    const idx = date === null ? 0 : bisectRight(this.dates, date)
    this.zoom(this.leftZoom, idx)
    this.rightZoomDateInput = date || this.lastDate || new Date()
  }

  @action readonly mainChartHandleMouseDown = (e: ChartMouseEvent | undefined) => {
    const {leftZoom} = this

    let x = e?.activeTooltipIndex

    if (x === undefined) {
      if (!isMobile) {
        this.zoomOut()
      }
      return
    }

    const {zoomedDataScale} = this

    x *= zoomedDataScale

    this.leftZoomPreview = x + leftZoom
  }

  @action readonly mainChartHandleMouseMove = (e?: ChartMouseEvent) => {
    if (!e) return

    let x = e.activeTooltipIndex

    if (x === undefined) {
      return
    }

    x *= this.zoomedDataScale

    if (this.zoomShiftInProgress) {
      this.zoomShiftEnd = x + this.leftZoomShiftStart!
      this.zoomShift()
      return
    }

    if (this.zoomInProgress) {
      this.rightZoomPreview = x + this.leftZoom
    }
  }

  @action readonly mainChartHandleMouseUp = () => {
    if (isMobile) {
      if (this.canZoomIn) {
        this.zoomIn()
      } else {
        this.stopAllZoomChangeInProgress()
      }
    } else {
      this.zoomIn()
    }
  }

  @action readonly miniMapHandleMouseDown = (e?: ChartMouseEvent) => {
    if (!e) return

    let x = e.activeTooltipIndex

    if (x === undefined) {
      this.zoomOut()
      return
    }

    x *= this.dataScale

    if (this.canStartZoomShift(x)) {
      this.leftZoomShiftStart = this.leftZoom
      this.rightZoomShiftStart = this.rightZoom
      this.zoomShiftStart = x
    } else {
      this.leftZoomPreview = x
    }
  }

  @action readonly miniMapHandleMouseMove = (e: ChartMouseEvent) => {
    if (!e) return

    let x = e.activeTooltipIndex

    if (x === undefined) {
      return
    }

    const {dataScale} = this;

    x *= dataScale

    if (this.isZoomed && this.zoomShiftInProgress) {
      this.zoomShiftEnd = x
      this.zoomShift()
      return
    }

    if (this.zoomInProgress) {
      this.rightZoomPreview = x
    }
  }

  @action readonly miniMapHandleMouseUp = () => {
    if (isMobile) {
      if (this.canZoomIn) {
        this.zoomIn()
      } else {
        this.stopAllZoomChangeInProgress()
      }
    } else {
      this.zoomIn()
    }
  }

  @action readonly setWidth = (width: number) => {
    this.width = width
  }
}

export function formatDateForInput(date: Date = new Date()) {
  const yyyy = date.getFullYear()
  const MM = (date.getMonth() + 1).toString().padStart(2, '0')
  const dd = (date.getDate()).toString().padStart(2, '0')
  return `${yyyy}-${MM}-${dd}`
}

const storeContext = React.createContext<ChartStore | null>(null)

export const ChartStoreProvider = observer(({store, children}: React.PropsWithChildren<{store: ChartStore}>) => {
  return <storeContext.Provider value={store}>{children}</storeContext.Provider>
})

export const useChartStore = () => {
  const store = React.useContext(storeContext)
  if (!store) {
    // this is especially useful in TypeScript so you don't need to be checking for null all the time
    throw new Error('useChartStore must be used within a ChartStoreProvider.')
  }
  return store
}
